import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { ApiError, auth, type Ucet } from '../api/client.js';

/**
 * Prihlasenie a odhlasenie.
 *
 * Relaciu drzi cookie `httpOnly`; tu sa pamäta len profil prihlaseneho
 * pouzivatela, aby rozhranie vedelo, co zobrazit. Rozhodnutie, ci smie
 * nieco urobit, robi VZDY server - skryvanie tlacidiel je pohodlie, nie
 * ochrana.
 */

export type StavRelacie = 'nacitava' | 'prihlaseny' | 'odhlaseny' | 'prvotne-nastavenie';

interface Relacia {
  readonly stav: StavRelacie;
  readonly user: Ucet | undefined;
  readonly chyba: string | undefined;
  login(username: string, password: string): Promise<void>;
  setup(token: string, username: string, password: string): Promise<void>;
  logout(): Promise<void>;
  /** Zavola sa, ked API oznami, ze relacia uz neplati. */
  vyprsala(): void;
}

const Kontext = createContext<Relacia | undefined>(undefined);

export function SessionProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [stav, setStav] = useState<StavRelacie>('nacitava');
  const [user, setUser] = useState<Ucet | undefined>(undefined);
  const [chyba, setChyba] = useState<string | undefined>(undefined);

  const zisti = useCallback(async () => {
    try {
      const { user: profil } = await auth.session();
      setUser(profil);
      setStav('prihlaseny');
    } catch (error) {
      if (!(error instanceof ApiError) || !error.needsLogin) {
        setChyba(error instanceof Error ? error.message : 'Neznáma chyba.');
      }
      setUser(undefined);
      // Bez uctu nema zmysel ponukat prihlasenie - treba prvotne nastavenie.
      try {
        const { setupPending } = await auth.status();
        setStav(setupPending ? 'prvotne-nastavenie' : 'odhlaseny');
      } catch {
        setStav('odhlaseny');
      }
    }
  }, []);

  useEffect(() => {
    void zisti();
  }, [zisti]);

  const login = useCallback(async (username: string, password: string) => {
    setChyba(undefined);
    const { user: profil } = await auth.login(username, password);
    setUser(profil);
    setStav('prihlaseny');
  }, []);

  const setup = useCallback(async (token: string, username: string, password: string) => {
    setChyba(undefined);
    const { user: profil } = await auth.setup(token, username, password);
    setUser(profil);
    setStav('prihlaseny');
  }, []);

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } finally {
      setUser(undefined);
      setStav('odhlaseny');
    }
  }, []);

  const vyprsala = useCallback(() => {
    setUser(undefined);
    setStav('odhlaseny');
  }, []);

  return (
    <Kontext.Provider value={{ stav, user, chyba, login, setup, logout, vyprsala }}>
      {children}
    </Kontext.Provider>
  );
}

export function useSession(): Relacia {
  const relacia = useContext(Kontext);
  if (!relacia) throw new Error('useSession sa dá použiť len vnútri SessionProvider.');
  return relacia;
}
